import { CartesianGrid, Line, LineChart, ReferenceLine, XAxis, YAxis } from 'recharts';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import { t } from '@/i18n';
import type { FinancialMonth } from '@ip/shared-types';
import { ChartCard } from './ChartCard';
import { MONTH_LABELS, pctTooltipFormatter } from './chart-utils';

interface MarginChartProps {
  months: FinancialMonth[];
  year: number;
}

export function MarginChart({ months, year }: MarginChartProps) {
  // Months with no revenue have marginPct === null; the line breaks there
  // (connectNulls off) instead of pretending the margin was 0%.
  const chartData = months.map((m) => ({
    month: MONTH_LABELS[m.month - 1],
    marginPct: m.marginPct,
  }));

  const config: ChartConfig = {
    marginPct: { label: t('dashboard.financial.marginPct'), color: 'var(--chart-1)' },
  };

  return (
    <ChartCard title={t('dashboard.financial.marginPctTitle')}>
      <ChartContainer config={config} className="h-[300px] w-full">
        <LineChart data={chartData}>
          <CartesianGrid vertical={false} strokeOpacity={0.4} />
          <XAxis dataKey="month" tickLine={false} axisLine={false} />
          <YAxis
            tickFormatter={(v: number) => `${v.toLocaleString('pl-PL', { maximumFractionDigits: 0 })}%`}
            tickLine={false}
            axisLine={false}
            width={56}
            allowDecimals={false}
            tickCount={6}
            domain={[
              (dataMin: number) => Math.floor(Math.min(0, dataMin) / 10) * 10,
              (dataMax: number) => Math.ceil(Math.max(0, dataMax) / 10) * 10,
            ]}
          />
          <ReferenceLine y={0} stroke="var(--muted-foreground)" strokeOpacity={0.6} />
          <ChartTooltip
            content={
              <ChartTooltipContent
                labelFormatter={(label) => `${label} ${year}`}
                formatter={pctTooltipFormatter(config)}
              />
            }
          />
          <Line
            type="monotone"
            dataKey="marginPct"
            stroke="var(--color-marginPct)"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            dot={false}
            activeDot={{ r: 4, strokeWidth: 2, stroke: 'var(--card)' }}
            connectNulls={false}
          />
        </LineChart>
      </ChartContainer>
    </ChartCard>
  );
}
